import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import crmApi, { STAGE_META, STAGE_ORDER, fmtDateTime, ACTIVITY_TYPES } from '../../utils/crmApi';
import useLiveRefresh from '../../utils/useLiveRefresh';

export default function CrmActivities() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fType, setFType] = useState('');
  const [fUser, setFUser] = useState('');
  const [fStage, setFStage] = useState('');
  const [fDeal, setFDeal] = useState('');
  const [deals, setDeals] = useState([]);
  const nowY = new Date().getFullYear();
  const [fYear, setFYear] = useState('all');
  const navigate = useNavigate();

  function load(silent) {
    if (!silent) setLoading(true);
    const params = new URLSearchParams();
    if (fType) params.set('activity_type', fType);
    if (fUser) params.set('user_id', fUser);
    if (fStage) params.set('stage', fStage);
    if (fDeal) params.set('deal_id', fDeal);
    if (fYear !== 'all') params.set('year', fYear);
    crmApi.get(`/activities?${params.toString()}`).then(setRows).finally(() => { if (!silent) setLoading(false); });
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fType, fUser, fStage, fDeal, fYear]);
  useLiveRefresh(() => load(true));
  useEffect(() => { crmApi.get('/users').then(u => setUsers(Array.isArray(u) ? u : [])).catch(() => {}); }, []);
  useEffect(() => { crmApi.get('/deals').then(d => setDeals(Array.isArray(d) ? d : [])).catch(() => {}); }, []);

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <h1 style={{ fontSize: '20px', fontWeight: '800' }}>Team Activity</h1>
        <p style={{ fontSize: '13px', color: 'var(--muted)' }}>What the team logged on deals — filter by activity, person or stage.</p>
      </div>

      {/* Filters */}
      <div className="card" style={{ padding: '14px', marginBottom: '14px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ minWidth: '120px' }}>
          <label className="form-label">Year</label>
          <select className="select" value={fYear} onChange={e => setFYear(e.target.value)}>
            <option value="all">All years</option>
            {Array.from({ length: (2035 - 2022) + 1 }, (_, i) => 2035 - i).map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div style={{ minWidth: '160px' }}>
          <label className="form-label">Activity type</label>
          <select className="select" value={fType} onChange={e => setFType(e.target.value)}>
            <option value="">All activities</option>
            {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div style={{ minWidth: '160px' }}>
          <label className="form-label">Team member</label>
          <select className="select" value={fUser} onChange={e => setFUser(e.target.value)}>
            <option value="">Everyone</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
        </div>
        <div style={{ minWidth: '160px' }}>
          <label className="form-label">Stage</label>
          <select className="select" value={fStage} onChange={e => setFStage(e.target.value)}>
            <option value="">All stages</option>
            {STAGE_ORDER.map(s => <option key={s} value={s}>{STAGE_META[s].label}</option>)}
          </select>
        </div>
        <div style={{ minWidth: '180px' }}>
          <label className="form-label">Deal</label>
          <select className="select" value={fDeal} onChange={e => setFDeal(e.target.value)}>
            <option value="">All deals</option>
            {deals.map(d => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select>
        </div>
        {(fType || fUser || fStage || fDeal) && (
          <button className="btn btn-sm" onClick={() => { setFType(''); setFUser(''); setFStage(''); setFDeal(''); }}>Clear filters</button>
        )}
        <div style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--muted)' }}>{rows.length} activities</div>
      </div>

      {/* KPI cards (respond to filters) */}
      {(() => {
        const total = rows.length;
        const deal_set = new Set(rows.map(r => r.deal_id));
        const people = {}, types = {};
        rows.forEach(r => {
          people[r.user_name] = (people[r.user_name] || 0) + 1;
          const t = r.activity_type || 'Note';
          types[t] = (types[t] || 0) + 1;
        });
        const topPeople = Object.entries(people).sort((a, b) => b[1] - a[1]).slice(0, 5);
        const topType = Object.entries(types).sort((a, b) => b[1] - a[1])[0];
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.6fr', gap: '12px', marginBottom: '16px' }}>
            <div className="card" style={{ padding: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase' }}>Total Actions</div>
              <div style={{ fontSize: '26px', fontWeight: '800', marginTop: '4px' }}>{total}</div>
            </div>
            <div className="card" style={{ padding: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase' }}>Deals Touched</div>
              <div style={{ fontSize: '26px', fontWeight: '800', marginTop: '4px' }}>{deal_set.size}</div>
            </div>
            <div className="card" style={{ padding: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase' }}>Top Activity</div>
              <div style={{ fontSize: '18px', fontWeight: '800', marginTop: '6px' }}>{topType ? topType[0] : '—'}</div>
              <div style={{ fontSize: '11px', color: 'var(--muted)' }}>{topType ? `${topType[1]} times` : ''}</div>
            </div>
            <div className="card" style={{ padding: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase', marginBottom: '8px' }}>By Team Member</div>
              {topPeople.length === 0 ? <div style={{ fontSize: '12px', color: 'var(--muted)' }}>No activity.</div> :
                topPeople.map(([name, n]) => {
                  const max = topPeople[0][1];
                  return (
                    <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                      <div style={{ fontSize: '11px', width: '110px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                      <div style={{ flex: 1, height: '8px', background: 'var(--surface2)', borderRadius: '99px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(n / max) * 100}%`, background: '#0D9488', borderRadius: '99px' }} />
                      </div>
                      <div style={{ fontSize: '11px', fontWeight: '700', width: '26px', textAlign: 'right' }}>{n}</div>
                    </div>
                  );
                })}
            </div>
          </div>
        );
      })()}

      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 1.4fr 130px 150px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
          {['Activity', 'Deal', 'Activity Details', 'Stage', 'Logged By / When'].map(h => (
            <div key={h} style={{ padding: '10px 14px', fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>{h}</div>
          ))}
        </div>
        {loading ? <div style={{ padding: '20px', color: 'var(--muted)' }}>Loading…</div> :
          rows.length === 0 ? <div style={{ padding: '24px', textAlign: 'center', color: 'var(--muted)', fontSize: '13px' }}>No activities match these filters.</div> :
            rows.map((r, i) => (
              <div key={r.id} onClick={() => navigate(`/crm/deals/${r.deal_id}`)}
                style={{ display: 'grid', gridTemplateColumns: '130px 1fr 1.4fr 130px 150px', borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ padding: '11px 14px' }}>
                  <span style={{ fontSize: '11px', fontWeight: '700', padding: '3px 9px', borderRadius: '99px', background: 'rgba(13,148,136,0.12)', color: '#0D9488' }}>{r.activity_type || 'Note'}</span>
                </div>
                <div style={{ padding: '11px 14px', fontSize: '13px', fontWeight: '600' }}>{r.deal_title}<div style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: '400' }}>{r.account_name || 'No account'}</div></div>
                <div style={{ padding: '11px 14px', fontSize: '12px', color: 'var(--muted2)' }}>{r.note}</div>
                <div style={{ padding: '11px 14px' }}>
                  {r.stage && <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 9px', borderRadius: '99px', background: STAGE_META[r.stage]?.bg, color: STAGE_META[r.stage]?.color }}>{STAGE_META[r.stage]?.label}</span>}
                </div>
                <div style={{ padding: '11px 14px', fontSize: '11px', color: 'var(--muted)' }}>{r.user_name}<br />{fmtDateTime(r.created_at)}</div>
              </div>
            ))}
      </div>
    </div>
  );
}
